import type { RefObject } from 'react';

import useEventListener from './useEventListener';

type Handler = (event: MouseEvent) => void;

/**
 * Invokes the handler callback if the mouse event click is outside the specified elements
 *
 * @note
 * This is basically expanded version of `useOnClickOutside` from `@hummingbird/shared` that accepts array as well
 */
const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
  refOrArray: RefObject<T> | RefObject<T>[],
  handler: Handler,
  mouseEvent: 'mousedown' | 'mouseup' = 'mousedown',
): void => {
  useEventListener(mouseEvent, event => {
    const refs = Array.isArray(refOrArray) ? refOrArray : [refOrArray];

    const areAllRefsNull = refs.every(ref => !ref.current);

    if (areAllRefsNull) return;

    const isClickedInside = refs.some(
      ref => !!ref.current && ref.current.contains(event.target as Node),
    );

    if (isClickedInside) {
      return;
    }

    handler(event);
  });
};

export default useOnClickOutside;
